import { useEffect } from 'react';

import type { ToastState } from '../state/store.js';

/**
 * The transient notice (design 1609–1614).
 *
 * Three seconds, `pointer-events: none`. It carries what has *just happened* and
 * has no resting place of its own — a resume replaying a gap, a submission the
 * server has acknowledged. Anything that stays true is an alert or a piece of
 * chrome instead; a toast that mattered after three seconds would be a toast
 * somebody missed.
 *
 * **Top centre of the centre pane, not the design's bottom centre.** The design
 * pins it at `bottom: 44px`, which is clear of the status bar and directly on
 * top of the orders-queue dock that Phase 3 put there — so at 1280 an
 * `ORDER QUEUED` notice landed across a card at the exact moment one appeared,
 * and "did that card just change?" is the wrong question to provoke under a
 * five-minute clock.
 *
 * It lives in the centre pane for the reason Phase 1 put the alert lane there
 * (finding 8): two of the console's chrome bands are conditional, so anything
 * positioned against the *console's* top edge lands somewhere different
 * depending on whether the read-only banner and the GM strip are up. The caller
 * supplies `top`, because the one thing it has to clear is the alert lane —
 * which owns this corner and outranks it. An alert is a condition; a toast is a
 * receipt.
 */

export const TOAST_MS = 3_000;

/** Clear of the pane's top edge when the alert lane is empty. */
export const TOAST_TOP = 12;

/**
 * Clear of one alert when it is not.
 *
 * A banner is a title over one short sentence — the copy rules allow it no more
 * — so its height is bounded and this is the bound, not a measurement. A second
 * simultaneous alert is rare enough that the toast sliding under it for three
 * seconds is the right trade against measuring a floating element on every
 * render.
 */
export const TOAST_TOP_WITH_ALERT = 76;

export interface ToastProps {
  readonly toast: ToastState | null;
  readonly onExpire: (id: number) => void;
  /** Distance from the top of the pane. `TOAST_TOP`, or past an alert. */
  readonly top: number;
}

export function Toast(props: ToastProps): JSX.Element | null {
  const { toast, onExpire } = props;
  const id = toast?.id ?? null;

  useEffect(() => {
    if (id === null) return;
    const timer = setTimeout(() => onExpire(id), TOAST_MS);
    return () => clearTimeout(timer);
  }, [id, onExpire]);

  if (toast === null) return null;
  const edge = toast.tone === 'warn' ? 'var(--warn)' : 'var(--ok)';

  return (
    <div
      data-testid="toast"
      role="status"
      style={{
        position: 'absolute',
        left: '50%',
        top: props.top,
        zIndex: 'var(--z-toast)',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        height: 30,
        padding: '0 14px',
        background: 'var(--surface-3)',
        border: `1px solid ${edge}`,
        boxShadow: 'var(--shadow)',
        pointerEvents: 'none',
        animation: 'bs-arrive .18s var(--ease)',
      }}
    >
      <span style={{ flex: 'none', width: 4, height: 14, background: edge }} />
      <span
        style={{
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '.1em',
          whiteSpace: 'nowrap',
          color: 'var(--text)',
        }}
      >
        {toast.text}
      </span>
    </div>
  );
}
